/**
 * Centralized appliance image quality rule.
 *
 * The user's requirement: every appliance image must show the appliance
 * occupying ≥75% of the frame, with the appliance as the only thing in shot.
 * We can't measure pixels client-side without downloading every image, so we
 * approximate by URL signals — patterns and dimensions that distinguish
 * "packshot / cutout / product shot" from "lifestyle / energy label / placeholder".
 *
 * Returns true when an image URL is missing or doesn't meet the bar
 * (caller should re-fetch a better one).
 */

// URL patterns that strongly suggest a product shot (>75% appliance coverage)
const productShotPatterns: RegExp[] = [
  /product[_\-]?shot/i,
  /packshot/i,
  /cutout/i,
  /product[_\-]?image/i,
  /\d+x\d+.*def/i,            // BSH "WAE..._def.webp" pattern
  /front[_\-]?view/i,
  /main[_\-]?image/i,
  /_xl\./i,
  /_large\./i,
  /hero/i,
  /5120x/i,
  /Product_Shots/i,           // BSH CDN
  /960x1320/i,
  /assets\.mmsrg\.com\/isr\//i, // MediaMarkt cutouts
];

// URL patterns indicating lifestyle, label, placeholder or wrong content
const rejectPatterns: RegExp[] = [
  // Lifestyle / context
  /lifestyle/i,
  /kitchen/i,
  /room/i,
  /interior/i,
  /ambient/i,
  /context/i,
  /scene/i,
  /gallery/i,
  // Tiny / decorative
  /thumb/i,
  /\bsmall\b/i,
  /\bmini\b/i,
  /_xs\./i,
  /_s\./i,
  /\b50x\b/i,
  /\b100x\b/i,
  /\b150x\b/i,
  /icon/i,
  /favicon/i,
  /logo(?!ut)/i,
  // Placeholders
  /placeholder/i,
  /no[_\-]?image/i,
  /image[_\-]?not[_\-]?available/i,
  // Energy labels & datasheets (never a product shot)
  /energy[_\-]?label/i,
  /energylabel/i,
  /energy[_\-]?rating/i,
  /energy[_\-]?class/i,
  /\beek\b/i,
  /eu[_\-]?label/i,
  /eulabel/i,
  /eprel/i,
  /fiche/i,
  /datasheet/i,
  /label[_\-]?image/i,
  /rating[_\-]?label/i,
  /media\.miele\.com\/downloads\//i,
];

// Hosts known to serve "no image available" placeholders or non-product content.
// Empty for now — fingerprint detection happens server-side; here we only
// catch obvious patterns.
const rejectHosts: string[] = [];

export function needsImageUpgrade(imageUrl?: string | null): boolean {
  if (!imageUrl) return true;
  const url = String(imageUrl).trim();
  if (!url) return true;

  // Skip local imports / data URIs / blobs — these are intentional fallbacks.
  if (url.startsWith("data:") || url.startsWith("blob:")) return true;
  // Local bundled assets (e.g. /assets/beko-b5t-dryer.webp) are pre-vetted — keep them.
  if (url.startsWith("/") && !url.startsWith("//")) return false;

  // Hard rejects
  if (rejectPatterns.some((p) => p.test(url))) return true;
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (rejectHosts.some((h) => host.includes(h))) return true;
  } catch {
    // Not a parseable URL → upgrade
    return true;
  }

  // Strong accept signals
  if (productShotPatterns.some((p) => p.test(url))) return false;

  // Dimension heuristic: long side ≥600px, aspect 0.5–2.0
  const sizeMatch = url.match(/(\d{3,4})x(\d{3,4})/);
  if (sizeMatch) {
    const w = parseInt(sizeMatch[1], 10);
    const h = parseInt(sizeMatch[2], 10);
    const ratio = w / h;
    if (Math.max(w, h) >= 600 && ratio >= 0.5 && ratio <= 2.0) return false;
    // Tiny or panoramic → upgrade
    return true;
  }

  // No clear signal → assume not optimal, request an upgrade
  return true;
}

export function isAcceptableProductImage(imageUrl?: string | null): boolean {
  return !needsImageUpgrade(imageUrl);
}
